/**
 * Categories and their declared attributes.
 *
 * The tree is read whole (`GET /categories`) rather than paged — a category tree is a few dozen
 * nodes at most, and a tree UI needs the whole shape to render at all. Any write invalidates the
 * whole tree tag; the tree changes rarely enough that a wider refetch costs nothing.
 *
 * Attribute invalidation is by bare type, not by category id: a category's own attribute list
 * depends on every ancestor's declarations too (inheritance), so a declare/update/set-identifying
 * on any category could change what a descendant's variant form shows. Refetching every open
 * attribute list is the honest invalidation here, not a narrower one that might miss a case.
 */

import { api } from '../api.js';
import { categoryAttributesTag, categoryTreeTag } from '../tags.js';
import type { AttributeDataType, AttributeId, CategoryId, CategoryNode, ResolvedAttribute } from '../../types/api.js';

export type CreateCategoryBody = {
  readonly parentId?: CategoryId | undefined;
  readonly name: string;
  readonly skuPrefix?: string | undefined;
  readonly sortOrder: number;
};

export type UpdateCategoryBody = {
  readonly name: string;
  readonly skuPrefix?: string | undefined;
  readonly sortOrder: number;
};

export type DeclareAttributeBody = {
  readonly key: string;
  readonly label: string;
  readonly dataType: AttributeDataType;
  readonly options?: readonly string[] | undefined;
  readonly unit?: string | undefined;
  readonly isRequired: boolean;
  readonly isFilterable: boolean;
  readonly isIdentifying: boolean;
  readonly sortOrder: number;
};

export type UpdateAttributeBody = {
  readonly label: string;
  readonly options?: readonly string[] | undefined;
  readonly unit?: string | undefined;
  readonly isRequired: boolean;
  readonly isFilterable: boolean;
  readonly sortOrder: number;
};

export const categoriesApi = api.injectEndpoints({
  endpoints: (build) => ({
    listCategories: build.query<CategoryNode[], { includeInactive?: boolean | undefined } | void>({
      query: (params) => ({ url: '/categories', params: params ?? {} }),
      providesTags: [categoryTreeTag()],
    }),

    createCategory: build.mutation<CategoryId, CreateCategoryBody>({
      query: (body) => ({ url: '/categories', method: 'POST', body }),
      invalidatesTags: [categoryTreeTag()],
    }),

    updateCategory: build.mutation<void, { id: CategoryId; body: UpdateCategoryBody }>({
      query: ({ id, body }) => ({ url: `/categories/${id}`, method: 'PUT', body }),
      invalidatesTags: [categoryTreeTag()],
    }),

    moveCategory: build.mutation<void, { id: CategoryId; newParentId: CategoryId | null }>({
      query: ({ id, newParentId }) => ({
        url: `/categories/${id}/move`,
        method: 'POST',
        body: { newParentId },
      }),
      invalidatesTags: [categoryTreeTag()],
    }),

    setCategoryActive: build.mutation<void, { id: CategoryId; isActive: boolean }>({
      query: ({ id, isActive }) => ({
        url: `/categories/${id}/activation`,
        method: 'POST',
        body: { isActive },
      }),
      invalidatesTags: [categoryTreeTag()],
    }),

    listCategoryAttributes: build.query<ResolvedAttribute[], CategoryId>({
      query: (id) => `/categories/${id}/attributes`,
      providesTags: (_result, _error, id) => [categoryAttributesTag(id)],
    }),

    declareAttribute: build.mutation<AttributeId, { categoryId: CategoryId; body: DeclareAttributeBody }>({
      query: ({ categoryId, body }) => ({
        url: `/categories/${categoryId}/attributes`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'CategoryAttributes' }],
    }),

    updateAttribute: build.mutation<void, { id: AttributeId; body: UpdateAttributeBody }>({
      query: ({ id, body }) => ({ url: `/category-attributes/${id}`, method: 'PUT', body }),
      invalidatesTags: [{ type: 'CategoryAttributes' }],
    }),

    /** Recomputes every affected variant's fingerprint server-side; returns how many. */
    setAttributeIdentifying: build.mutation<number, { id: AttributeId; isIdentifying: boolean }>({
      query: ({ id, isIdentifying }) => ({
        url: `/category-attributes/${id}/identifying`,
        method: 'PUT',
        body: { isIdentifying },
      }),
      invalidatesTags: [{ type: 'CategoryAttributes' }],
    }),
  }),
});

export const {
  useListCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useMoveCategoryMutation,
  useSetCategoryActiveMutation,
  useListCategoryAttributesQuery,
  useDeclareAttributeMutation,
  useUpdateAttributeMutation,
  useSetAttributeIdentifyingMutation,
} = categoriesApi;
